import { lazy } from 'react'
import { Translation } from 'react-i18next'
import { Navigate, Outlet, Route } from 'react-router-dom'

import { typeUserViewScheduleEnum } from '@/modules/schedule/domain/models/TypeUserViewSchedule'
import SituationalIcon from '@/shared/components/icons/situational-icon'
import BathroomHangerIcon from '@/ui/assets/BathroomHangerIcon'
import BodyInspectionIcon from '@/ui/assets/BodyInspectionIcon'
import CalendarPersonIcon from '@/ui/assets/CalendarPersonIcon'
import ChatMessageAddSquareIcon from '@/ui/assets/ChatMessageAddSquareIcon'
import ChatMessageIcon from '@/ui/assets/ChatMessageIcon'
import CommentsNewIcon from '@/ui/assets/CommentsNewIcon'
import FolderImageIcon from '@/ui/assets/FolderImageIcon'
import FoodIcon from '@/ui/assets/FoodIcon'
import HomeIcon from '@/ui/assets/HomeIcon'
import LiquidIcon from '@/ui/assets/LiquidIcon'
import MedicalRecord from '@/ui/assets/MedicalRecord'
import PersonIcon from '@/ui/assets/PersonIcon'
import RemediesIcon from '@/ui/assets/RemediesIcon'
import SleepStarCircleIcon from '@/ui/assets/SleepStarCircleIcon'
import TicketPaperIcon from '@/ui/assets/TicketPaperIcon'
import ToyIcon from '@/ui/assets/ToyIcon'
import LocationBasedRemountComponent from '@/ui/components/LocationBasedRemountComponent'
import PatientProvider from '@/ui/contexts/PatientContext'
import { PresenceProvider } from '@/ui/contexts/PresenceContext'
import { TicketChatProvider } from '@/ui/contexts/TicketChatContext'
import TicketEvaluationProvider from '@/ui/contexts/TicketEvaluationContext'
import { FeatureFlagManager } from '@/ui/hoc/FeatureFlag/FeatureFlagManager'
import { FeatureFlagManagerWithDefaultPage } from '@/ui/hoc/FeatureFlag/FeatureFlagManagerWithDefaultPage'
import { MultiSelectionProvider } from '@/ui/hoc/MultiSelection'
import { UserPermissionManager } from '@/ui/hoc/UserPermission/UserPermissionManager'
import UserPermissionShowComponent from '@/ui/hoc/UserPermission/UserPermissionShowComponent'
import { BodyInspectionProvider } from '@/ui/modules/operational/views/BodyInspection/DailyTable/contexts/BodyInspectionFilterContext'
import {
  responsibleDynamicRoutes,
  responsibleRouteNames
} from '@/ui/routes/routesNames/responsibleRouteNames'

import FoodStockProvider from '../modules/operational/contexts/FoodStockContext'

const TicketDetails = lazy(
  () => import('@/ui/modules/operational/views/TicketDetails')
)
const UserTicket = lazy(
  () => import('@/ui/modules/operational/views/UserTicket')
)

const Bathroom = lazy(() => import('@/ui/modules/operational/views/Bathroom'))
const Belongs = lazy(() => import('@/ui/modules/operational/views/Belongs'))
const Food = lazy(() => import('@operational-ui/views/Food'))
const Liquids = lazy(() => import('@/ui/modules/operational/views/Liquids'))
const PatientTickets = lazy(
  () => import('@/ui/modules/operational/views/PatientTickets')
)
const Comments = lazy(() => import('@/ui/modules/operational/views/Comments'))
const PatientFoodReport = lazy(
  () => import('@/ui/modules/operational/views/PatientFoodReport')
)
const PatientLayout = lazy(() => import('@/ui/layouts/PatientLayout'))
const PatientSelector = lazy(() => import('@ui/pages/PatientSelector'))
const Restriction = lazy(
  () => import('@/ui/modules/operational/views/Restriction')
)
const ExploreAndInvitations = lazy(
  () =>
    import(
      '@/therapist/pages/groups/PostGroup/components/ExploreAndInvitations'
    )
)
const Sleep = lazy(() => import('@/ui/modules/operational/views/Sleep'))
const Remedy = lazy(() => import('@/ui/modules/operational/views/Remedy'))
const InternalLayout = lazy(() => import('@ui/layouts/InternalLayout'))
const FoodScheduleProvider = lazy(
  () => import('@/ui/modules/operational/contexts/FoodScheduleTabsContext')
)
const Group = lazy(() => import('@/therapist/pages/groups'))
const PostDetails = lazy(() => import('@/therapist/pages/post-details'))
const Chat = lazy(() => import('@/therapist/pages/chat'))
const TicketChat = lazy(() => import('@/ui/modules/shared/TicketChat'))
const ProfileInfo = lazy(() => import('@/shared/pages/client'))
const Gallery = lazy(() => import('@/therapist/pages/patient-profile/gallery'))
const Schedule = lazy(
  () => import('@/therapist/pages/patient-profile/schedule')
)
const Profile = lazy(() => import('@/therapist/pages/profile'))
const BodyInspection = lazy(
  () => import('@/ui/modules/operational/views/BodyInspection')
)
const MedicalRecordPatient = lazy(
  () => import('@/therapist/pages/medicalRecord')
)

export function ResponsibleRoutes() {
  return (
    <>
      <Route
        element={
          <PresenceProvider>
            <TicketChatProvider>
              <TicketEvaluationProvider>
                <PatientProvider>
                  <Outlet />
                </PatientProvider>
              </TicketEvaluationProvider>
            </TicketChatProvider>
          </PresenceProvider>
        }
      >
        <Route
          path={responsibleRouteNames.home}
          element={<Navigate to={responsibleRouteNames.groups} replace />}
        />

        <Route
          element={
            <PatientProvider>
              <Translation>
                {(t) => (
                  <UserPermissionManager>
                    {({
                      checkPermissionAndReturn,
                      checkNotPermissionAndReturn
                    }) => (
                      <InternalLayout
                        homeLink={responsibleRouteNames.groups}
                        menuItems={[
                          {
                            name: t('internalLayout.general.home'),
                            icon: <HomeIcon />,
                            navigateTo: responsibleRouteNames.groups
                          },
                          ...checkPermissionAndReturn('PATIENT_LIST_VIEW', {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            navigateTo: responsibleRouteNames.patients
                          }),
                          {
                            name: t('internalLayout.operational.ticket'),
                            icon: <TicketPaperIcon />,
                            subMenu: [
                              {
                                name: t(
                                  'internalLayout.operational.openTicket'
                                ),
                                icon: <ChatMessageAddSquareIcon />,
                                navigateTo: responsibleRouteNames.ticket
                              }
                            ]
                          }
                        ]}
                        searchItems={[
                          {
                            name: 'Feed',
                            icon: <HomeIcon />,
                            route: responsibleRouteNames.groupDetails,
                            isNavigatable: false,
                            isSearchable: false
                          },
                          {
                            name: t('internalLayout.general.home'),
                            icon: <HomeIcon />,
                            route: responsibleRouteNames.groups,
                            children: [
                              {
                                name: 'Explorar e convites',
                                route: responsibleRouteNames.explore,
                                module: t('internalLayout.general.home')
                              }
                            ]
                          },
                          ...checkNotPermissionAndReturn('PATIENT_LIST_VIEW', {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            route: responsibleRouteNames.patients,
                            isSearchable: false,
                            isNavigatable: false,
                            children: [
                              {
                                name: t('patient.sidebar.items.gallery'),
                                icon: <FolderImageIcon />,
                                route: responsibleRouteNames.gallery,
                                isSearchable: false,
                                isNavigatable: false
                              }
                            ]
                          }),
                          ...checkPermissionAndReturn('PATIENT_LIST_VIEW', {
                            name: t('internalLayout.operational.patients'),
                            icon: <PersonIcon />,
                            route: responsibleRouteNames.patients,
                            children: [
                              {
                                name: t('patient.sidebar.items.food'),
                                icon: <FoodIcon />,
                                isSearchable: false,
                                children: [
                                  {
                                    name: t(
                                      'patient.sidebar.items.scheduleFood'
                                    ),
                                    route: responsibleRouteNames.food,
                                    module: t('patient.sidebar.items.food'),
                                    isSearchable: false
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.restrictionFood'
                                    ),
                                    route: responsibleRouteNames.restriction,
                                    module: t('patient.sidebar.items.food'),
                                    isSearchable: false
                                  }
                                ]
                              },
                              {
                                name: t('patient.sidebar.items.bathroom'),
                                icon: <BathroomHangerIcon />,
                                route: responsibleRouteNames.bathroom,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.tickets'),
                                icon: <TicketPaperIcon />,
                                route: responsibleRouteNames.patientTicket,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.comments'),
                                icon: <CommentsNewIcon />,
                                route: responsibleRouteNames.comments,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.gallery'),
                                icon: <FolderImageIcon />,
                                route: responsibleRouteNames.gallery,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.medicalRecord'),
                                icon: <MedicalRecord />,
                                route: responsibleRouteNames.medicalSheet,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.liquids'),
                                icon: <LiquidIcon />,
                                route: responsibleRouteNames.liquids,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.remedy'),
                                icon: <RemediesIcon />,
                                route: responsibleRouteNames.remedy,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.belongs'),
                                icon: <ToyIcon />,
                                route: responsibleRouteNames.patientBelongs,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.sleep'),
                                icon: <SleepStarCircleIcon />,
                                route: responsibleRouteNames.sleep,
                                isSearchable: false
                              },
                              {
                                name: t('patient.sidebar.items.bodyInspection'),
                                icon: <BodyInspectionIcon />,
                                route: responsibleRouteNames.bodyInspection,
                                isSearchable: false
                              }
                            ]
                          }),
                          {
                            name: t('internalLayout.therapist.ticket'),
                            children: [
                              {
                                name: 'Detalhes do chamado',
                                icon: <ChatMessageAddSquareIcon />,
                                route: responsibleRouteNames.ticketDetails,
                                module: t('internalLayout.therapist.ticket')
                              },
                              {
                                name: t('internalLayout.therapist.openTicket'),
                                icon: <ChatMessageAddSquareIcon />,
                                route: responsibleRouteNames.ticket,
                                module: t('internalLayout.therapist.ticket')
                              }
                            ]
                          },
                          {
                            name: 'Perfil',
                            icon: <PersonIcon />,
                            route: responsibleRouteNames.profileInfo,
                            isSearchable: false
                          },
                          {
                            name: 'Meu perfil',
                            icon: <PersonIcon />,
                            route: responsibleRouteNames.profile
                          },
                          {
                            name: 'Chat',
                            icon: <ChatMessageIcon />,
                            route: responsibleRouteNames.chat
                          },
                          {
                            name: 'Detalhes da publicação',
                            icon: <HomeIcon />,
                            route: responsibleRouteNames.postDetails,
                            isSearchable: false
                          }
                        ]}
                      />
                    )}
                  </UserPermissionManager>
                )}
              </Translation>
            </PatientProvider>
          }
        >
          <Route path={responsibleRouteNames.ticket} element={<UserTicket />} />
          <Route
            path={responsibleRouteNames.ticketDetails}
            element={<TicketDetails />}
          />
          <Route path={responsibleRouteNames.profile} element={<Profile />} />
          <Route
            path={responsibleRouteNames.profileInfo}
            element={<ProfileInfo />}
          />
          <Route
            path={responsibleRouteNames.postDetails}
            element={<PostDetails />}
          />
          <Route
            path={responsibleRouteNames.ticketChat}
            element={<TicketChat />}
          />
          <Route path={responsibleRouteNames.chat} element={<Chat />} />
          <Route
            path={responsibleRouteNames.groups}
            element={
              <LocationBasedRemountComponent stableParams={['filter']}>
                <Group />
              </LocationBasedRemountComponent>
            }
          />
          <Route
            path={responsibleRouteNames.explore}
            element={<ExploreAndInvitations />}
          />
          <Route
            path={responsibleRouteNames.patients}
            element={<PatientSelector personaType="RESPONSIBLE" />}
          />
          <Route
            element={
              <Translation>
                {(t) => (
                  <FeatureFlagManager>
                    {({ checkFlagAndReturn }) => (
                      <UserPermissionManager>
                        {({ checkPermissionAndReturn }) => (
                          <PatientLayout
                            menuItems={(patientId) => [
                              ...checkPermissionAndReturn('AGENDA_VIEW', {
                                name: t('patient.sidebar.items.schedule'),
                                icon: <CalendarPersonIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.schedule(patientId)
                              }),
                              ...checkFlagAndReturn('FOOD', {
                                name: t('patient.sidebar.items.food'),
                                icon: (
                                  <FoodIcon fontSize={'24'} strokeWidth={1} />
                                ),
                                subMenu: [
                                  {
                                    name: t(
                                      'patient.sidebar.items.scheduleFood'
                                    ),
                                    navigateTo:
                                      responsibleDynamicRoutes.food(patientId)
                                  },
                                  {
                                    name: t('patient.sidebar.items.foodReport'),
                                    navigateTo:
                                      responsibleDynamicRoutes.foodReport(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.restrictionFood'
                                    ),
                                    navigateTo:
                                      responsibleDynamicRoutes.restriction(
                                        patientId
                                      )
                                  }
                                ]
                              }),
                              ...checkFlagAndReturn('BATHROOM', {
                                name: t('patient.sidebar.items.bathroom'),
                                icon: <BathroomHangerIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.bathroom(patientId)
                              }),
                              {
                                name: t('patient.sidebar.items.tickets'),
                                icon: <TicketPaperIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.patientTicket(
                                    patientId
                                  )
                              },
                              {
                                name: t('patient.sidebar.items.comments'),
                                icon: <CommentsNewIcon />,
                                navigateTo:
                                  responsibleDynamicRoutes.comments(patientId)
                              },
                              ...checkPermissionAndReturn(
                                'MANAGER_DAILY_ACCOMPANIMENT',
                                {
                                  name: t('patient.sidebar.items.configClient'),
                                  icon: <SituationalIcon fontSize={'24'} />,
                                  navigateTo:
                                    responsibleDynamicRoutes.config(patientId)
                                }
                              ),
                              ...checkPermissionAndReturn('DRIVE_VIEW', {
                                name: t('patient.sidebar.items.gallery'),
                                icon: <FolderImageIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.gallery(patientId)
                              }),
                              {
                                name: t('patient.sidebar.items.medicalRecord'),
                                icon: <MedicalRecord fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.medicalSheet(
                                    patientId
                                  )
                              },
                              ...checkFlagAndReturn('LIQUIDS', {
                                name: t('patient.sidebar.items.liquids'),
                                icon: <LiquidIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.liquids(patientId)
                              }),
                              ...checkFlagAndReturn('MEDICINES', {
                                name: t('patient.sidebar.items.remedy'),
                                icon: <RemediesIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.remedy(patientId)
                              }),
                              ...checkFlagAndReturn('BELONGINGS', {
                                name: t('patient.sidebar.items.belongs'),
                                icon: <ToyIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.patientBelongs(
                                    patientId
                                  )
                              }),
                              ...checkFlagAndReturn('SLEEP', {
                                name: t('patient.sidebar.items.sleep'),
                                icon: <SleepStarCircleIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.sleep(patientId)
                              }),
                              ...checkFlagAndReturn('LESION', {
                                name: t('patient.sidebar.items.bodyInspection'),
                                icon: <BodyInspectionIcon fontSize={'24'} />,
                                navigateTo:
                                  responsibleDynamicRoutes.bodyInspection(
                                    patientId
                                  )
                              })
                            ]}
                          />
                        )}
                      </UserPermissionManager>
                    )}
                  </FeatureFlagManager>
                )}
              </Translation>
            }
          >
            <Route
              path={responsibleRouteNames.food}
              element={
                <FoodScheduleProvider>
                  <FoodStockProvider>
                    <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                      <Food />
                    </FeatureFlagManagerWithDefaultPage>
                  </FoodStockProvider>
                </FoodScheduleProvider>
              }
            />
            <Route
              path={responsibleRouteNames.restriction}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                  <Restriction />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.foodReport}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                  <PatientFoodReport />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.liquids}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['LIQUIDS']}>
                  <Liquids />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.bathroom}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BATHROOM']}>
                  <Bathroom />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.sleep}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['SLEEP']}>
                  <Sleep />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.patientTicket}
              element={<PatientTickets />}
            />
            <Route
              path={responsibleRouteNames.patientBelongs}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BELONGINGS']}>
                  <Belongs />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.remedy}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['MEDICINES']}>
                  <Remedy />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={responsibleRouteNames.schedule}
              element={
                <UserPermissionShowComponent permission="AGENDA_VIEW">
                  <Schedule
                    userViewType={typeUserViewScheduleEnum.enum.PATIENT}
                  />
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={responsibleRouteNames.gallery}
              element={
                <UserPermissionShowComponent permission="DRIVE_VIEW">
                  <MultiSelectionProvider>
                    <Gallery />
                  </MultiSelectionProvider>
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={responsibleRouteNames.comments}
              element={<Comments />}
            />
            <Route
              path={responsibleRouteNames.medicalSheet}
              element={
                <UserPermissionShowComponent permission="MANAGE_MEDICAL_RECORD">
                  <MedicalRecordPatient />
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={responsibleRouteNames.galleryItem}
              element={
                <UserPermissionShowComponent permission="DRIVE_VIEW">
                  <MultiSelectionProvider>
                    <Gallery />
                  </MultiSelectionProvider>
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={responsibleRouteNames.galleryFolder}
              element={
                <UserPermissionShowComponent permission="DRIVE_VIEW">
                  <MultiSelectionProvider>
                    <Gallery />
                  </MultiSelectionProvider>
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={responsibleRouteNames.bodyInspection}
              element={
                <BodyInspectionProvider>
                  <BodyInspection />
                </BodyInspectionProvider>
              }
            />
            <Route path={responsibleRouteNames.entryPatient} element={<></>} />
          </Route>
        </Route>
      </Route>
    </>
  )
}
